import React, { ReactNode, memo, useMemo } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import moment from 'moment'
import AddIcon from 'transfix-icons/build/AddIcon'
import WarningIcon from 'transfix-icons/build/WarningIcon'
import { Link } from 'transfix-ui/core'
import Typography from 'transfix-ui/core/Typography'
import { Maybe } from 'types/FMSGraphqlTypes'
import { BooleanParam } from 'use-query-params'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { useQueryParams } from 'utils/hooks'
import { FORMATS } from 'constants/datetime'
import { ICarrierProfilesPageParams } from '../../CarrierProfilesPage'
import { TEST_IDS } from '../../constants'
import { IMotorCarrierUserInfoFragment } from '../../graphql/MotorCarrierUserInfoFragment'
import CarrierInfoTooltip from './components/CarrierInfoTooltip'
import CarrierStatusTag from './components/CarrierStatusTag'
import MotorCarrierSelect, { IMotorCarrierSelectProps } from './components/MotorCarrierSelect'
import { IHeaderInfoFragment } from './graphql/HeaderInfoFragment'
import { useCarrierProfileHeaderStyles } from './useCarrierProfileHeaderStyles'

interface ICarrierInfoProps {
  className?: string
  children: ReactNode
  dataTest?: string
}

interface ITeamMembersDisplayProps {
  teamMembersLabel: string
}

const TeamMembersDisplay = ({ teamMembersLabel }: ITeamMembersDisplayProps) => {
  const [{ mobileApp }] = useQueryParams<ICarrierProfilesPageParams>({ mobileApp: BooleanParam })
  return mobileApp ? (
    <Typography variant='body2' component='div' data-test={TEST_IDS.teamLink}>
      {teamMembersLabel}
    </Typography>
  ) : (
    <RouterLink to='/team' data-test={TEST_IDS.teamLink}>
      {teamMembersLabel}
    </RouterLink>
  )
}

const CarrierInfo = memo(({ children, className, dataTest = '' }: ICarrierInfoProps) => {
  return (
    <Typography variant='body2' component='div' className={className} data-test={dataTest}>
      {children}
    </Typography>
  )
})

const Divider = memo(() => {
  const classes = useCarrierProfileHeaderStyles()
  return <div className={classes.divider} />
})

export interface ICarrierProfileHeaderProps extends IHeaderInfoFragment {
  userAddedAt: Maybe<IMotorCarrierUserInfoFragment['createdAt']>
  isJoinRequestPending: boolean
  motorCarrierSelectOptions: IMotorCarrierSelectProps['options']
  onCarrierSelect: IMotorCarrierSelectProps['onSelect']
  onAddCarriers: () => void
  onRemoveCarrier: () => void
}

const CarrierProfileHeader = memo<ICarrierProfileHeaderProps>(
  ({
    dotNumber,
    id: motorCarrierId,
    isJoinRequestPending,
    legalName,
    mcNumber,
    motorCarrierSelectOptions,
    onAddCarriers,
    onCarrierSelect,
    onRemoveCarrier,
    state,
    teamMemberCount,
    userAddedAt,
  }) => {
    const { isMobile } = useBreakpointContext()
    const classes = useCarrierProfileHeaderStyles()

    const [{ mobileApp }] = useQueryParams({ mobileApp: BooleanParam })

    const userAddedAtDate = moment(userAddedAt).format(FORMATS.monthDateFormat)

    const showContactSupportMessage = !isJoinRequestPending && state === null
    const showAddTeamMembersButton = teamMemberCount === 0

    const teamMembersLabel = useMemo(() => {
      return `${teamMemberCount} Team ${teamMemberCount === 1 ? 'Member' : 'Members'}`
    }, [teamMemberCount])

    const inReviewClass = isJoinRequestPending ? classes.inReview : ''

    const showMotorCarrierSelect = motorCarrierSelectOptions.length > 1

    return (
      <div className={classes.header}>
        <div className={classes.top}>
          <div className={classes.nameWrapper}>
            <Typography variant='h3' className={inReviewClass} data-test={TEST_IDS.carrierName}>
              {legalName}
            </Typography>
            {isJoinRequestPending && <CarrierInfoTooltip />}
          </div>
          <div className={classes.carrierAction}>
            {showMotorCarrierSelect ? (
              <MotorCarrierSelect
                options={motorCarrierSelectOptions}
                selected={motorCarrierId}
                onSelect={onCarrierSelect}
              />
            ) : (
              !mobileApp && (
                <Link
                  className={classes.iconLink}
                  onClick={onAddCarriers}
                  data-test={TEST_IDS.addCarrierLink}>
                  <AddIcon size='medium' className={classes.mr} />
                  <span>Add Carriers</span>
                </Link>
              )
            )}
          </div>
        </div>
        <div className={classes.infoWrapper}>
          {mcNumber && (
            <>
              <CarrierInfo className={inReviewClass} dataTest={TEST_IDS.carrierMC}>
                MC {mcNumber}
              </CarrierInfo>
              <Divider />
            </>
          )}
          <CarrierInfo className={inReviewClass} dataTest={TEST_IDS.carrierDOT}>
            DOT {dotNumber}
          </CarrierInfo>
          {!isJoinRequestPending && (
            <div className={classes.teamInfo}>
              {!isMobile && <Divider />}
              {showAddTeamMembersButton ? (
                <>
                  <CarrierInfo className={inReviewClass} dataTest={TEST_IDS.teamMemberCount}>
                    {teamMembersLabel}
                  </CarrierInfo>
                  <RouterLink to='/team' className={classes.ml} data-test={TEST_IDS.addTeamLink}>
                    Add Team
                  </RouterLink>
                </>
              ) : (
                <TeamMembersDisplay teamMembersLabel={teamMembersLabel} />
              )}
            </div>
          )}
        </div>
        {!isJoinRequestPending && (
          <div className={classes.bottom} data-test={TEST_IDS.carrierStatusHeaderSection}>
            <div className={classes.statusWrapper}>
              <CarrierStatusTag status={state} className={classes.mr} />
              <Typography
                variant='caption'
                color='textSecondary'
                component='div'
                data-test={TEST_IDS.userAddedAt}
                className={classes.mr}>
                Added {userAddedAtDate}
              </Typography>
            </div>
            {!isJoinRequestPending && (
              <Link onClick={onRemoveCarrier} data-test={TEST_IDS.removeCarrierLink}>
                Remove Carrier
              </Link>
            )}
          </div>
        )}
        {showContactSupportMessage && (
          <div className={classes.contactSupport} data-test={TEST_IDS.contactSupportMessage}>
            <WarningIcon size='medium' />
            <Typography variant='body2' color='textSecondary'>
              Please contact support to complete the activation of your carrier profile.
            </Typography>
          </div>
        )}
      </div>
    )
  }
)

export default CarrierProfileHeader
